import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import {
	CellulaFormSubmitData,
	Flex,
	Form,
	InputCheckbox,
	InputCheckboxGroup,
	InputRadio,
	InputSelect,
	InputTextArea,
	useFormContext,
} from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { ReactNode, useEffect, useRef, useState } from 'react';
import type {
	FormPage,
	FormQuestionTypeMultiChoice,
	FormQuestionTypeMultiline,
	FormQuestionTypeSingleChoiceRadio,
	FormQuestionTypeSingleChoiceSelect,
	FormQuestionTypeYesNo,
	FormWorker,
	QuestionUnion,
} from './form_manager/mod';
import { useFormWorkerState } from './form_manager/use_form_worker';

interface Props {
	page: FormPage;
	formWorker: Readonly<FormWorker>;
	children: ReactNode;
}

export function DynamicFormPage({ page, formWorker, children }: Props): JSX.Element | null {
	const ref = useRef<HTMLDivElement | null>(null);
	const [defaultValues, setDefaultValues] = useState<CellulaFormSubmitData>({});
	const [questions, setQuestions] = useState<ReactNode[]>([]);

	useEffect(
		function whenCurrentStepChanges() {
			ref.current?.focus();

			const defaultValues: CellulaFormSubmitData = {};

			setQuestions(
				page.questions.map((question) => {
					defaultValues[question.id] = question.defaultValue ?? undefined;

					return <QuestionComp key={question.id} formWorker={formWorker} question={question} />;
				}),
			);
			setDefaultValues(defaultValues);
		},
		[page, formWorker],
	);

	async function handleSubmit(data: CellulaFormSubmitData): Promise<void> {
		try {
			await formWorker.changePage('forwards', data);
		} catch {
			throw new Error('Something went wrong.. Please try again!');
		}
	}

	return (
		<Form.Root onSubmit={handleSubmit} options={{ defaultValues }}>
			<InnerForm defaultValues={defaultValues}>{questions}</InnerForm>

			<Form.Error />
			{children}
		</Form.Root>
	);
}

function InnerForm({ defaultValues, children }: { defaultValues: CellulaFormSubmitData; children: ReactNode }): JSX.Element | null {
	const formContext = useFormContext();

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function whenDefaultValuesChange() {
			formContext?.control.reset(defaultValues);
		},
		// Reason, we only want to run this effect once, when the defaultValues change.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[defaultValues],
	);

	return <Flex gap={tokens.spacing.x2}>{children}</Flex>;
}

type WithCommonProps = {
	formWorker: Readonly<FormWorker>;
};

function QuestionComp({ question, formWorker }: { question: QuestionUnion } & WithCommonProps): JSX.Element {
	switch (question.type) {
		case 'YesNo':
			return <YesNo question={question} formWorker={formWorker} />;
		case 'MultiChoice':
			return <MultiChoice question={question} formWorker={formWorker} />;
		case 'Multiline':
			return <Multiline question={question} formWorker={formWorker} />;
		case 'SingleChoiceRadio':
			return <SingleChoiceRadio question={question} formWorker={formWorker} />;
		case 'SingleChoiceSelect':
			return <SingleChoiceSelect question={question} formWorker={formWorker} />;
		default:
			throw new Error(`Could not resolve a component type: {question.type}`);
	}
}

function YesNo({ question, formWorker }: { question: FormQuestionTypeYesNo } & WithCommonProps): JSX.Element | null {
	const { completed, editable } = useFormWorkerState(formWorker);

	return (
		<InputRadio.Root
			required={question.isRequired}
			readOnly={completed && !editable}
			name={question.id}
			label={question.header}
			options={question.isRequired ? { required: I18nAndL10n.translate('Form.ValidationError.OnlyOneOption') } : undefined}
		>
			<InputRadio.Item value='true' label={question.trueLabel} />
			<InputRadio.Item value='false' label={question.falseLabel} />
		</InputRadio.Root>
	);
}

function Multiline({ question, formWorker }: { question: FormQuestionTypeMultiline } & WithCommonProps): JSX.Element | null {
	const { completed, editable } = useFormWorkerState(formWorker);

	return (
		<InputTextArea
			label={question.header}
			required={question.isRequired}
			readOnly={completed && !editable}
			name={question.id}
			options={question.isRequired ? { required: I18nAndL10n.translate('Form.ValidationError.Required') } : undefined}
			minHeight={120}
			placeholder={question.placeholder}
		></InputTextArea>
	);
}

function MultiChoice({ question, formWorker }: { question: FormQuestionTypeMultiChoice } & WithCommonProps): JSX.Element | null {
	const { completed, editable } = useFormWorkerState(formWorker);

	return (
		<InputCheckboxGroup
			label={question.header}
			name={question.id}
			readOnly={completed && !editable}
			required={question.isRequired}
			options={question.isRequired ? { required: I18nAndL10n.translate('Form.ValidationError.MinOneOption') } : undefined}
		>
			{question.options.map((option) => (
				<InputCheckbox
					key={option.id}
					value={option.id}
					label={option.text}
					defaultChecked={question.defaultValue?.includes(option.id)}
				/>
			))}
		</InputCheckboxGroup>
	);
}

function SingleChoiceSelect({
	question,
	formWorker,
}: { question: FormQuestionTypeSingleChoiceSelect } & WithCommonProps): JSX.Element | null {
	const { completed, editable } = useFormWorkerState(formWorker);

	return (
		<InputSelect.Root
			label={question.header}
			required={question.isRequired}
			readOnly={completed && !editable}
			name={question.id}
			placeholder={withTranslation('Feature.DynamicForms.SingleChoiceSelect.Placeholder')}
			options={question.isRequired ? { required: I18nAndL10n.translate('Form.ValidationError.OnlyOneOption') } : undefined}
		>
			{question.options.map((option) => (
				<InputSelect.Item key={option.id} value={option.id}>
					{option.text}
				</InputSelect.Item>
			))}
		</InputSelect.Root>
	);
}

function SingleChoiceRadio({
	question,
	formWorker,
}: { question: FormQuestionTypeSingleChoiceRadio } & WithCommonProps): JSX.Element | null {
	const { completed, editable } = useFormWorkerState(formWorker);

	return (
		<InputRadio.Root
			required={question.isRequired}
			readOnly={completed && !editable}
			name={question.id}
			label={question.header}
			options={question.isRequired ? { required: I18nAndL10n.translate('Form.ValidationError.OnlyOneOption') } : undefined}
		>
			{question.options.map((option) => (
				<InputRadio.Item key={option.id} value={option.id} label={option.text} />
			))}
		</InputRadio.Root>
	);
}
